import { z } from "zod";
import { isoDateSchema as isoDate, reportFilterSchema } from "./report-filter.js";
/**
 * Opportunities: what to do next for a report's brand (DESIGN.md §9,
 * "Opportunities"). Rust only: `server/crates/api/src/modules/opportunities`.
 *
 * Every figure here is a count — of answers, citations or questions — and the
 * rule that put an item on the list is a threshold on those counts, written
 * once on the server. Nothing here was written by a model (AGENTS.md §14a): a
 * model's reading of the pages behind an item is asked for separately, and
 * labelled as such where it is shown.
 */
export const opportunitiesFilterSchema = reportFilterSchema;
const count = z.number().int().min(0);
/** A brand or company on one side of a comparison. */
export const opportunityPartySchema = z.object({
    name: z.string(),
    /** A tracked brand's id; null for a company only the answers name. */
    brandId: z.string().nullable(),
    /** A company's name key; null for a tracked brand. */
    entityKey: z.string().nullable(),
    /** Answers that name it. */
    answers: count,
});
const keyed = { key: z.string().min(1) };
export const opportunitySchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("name_to_count"),
        ...keyed,
        name: z.string(),
        entityKey: z.string(),
        answers: count,
        questions: count,
        brandId: z.string(),
        brandName: z.string(),
    }),
    z.object({
        kind: z.literal("question_to_win"),
        ...keyed,
        promptId: z.string(),
        text: z.string(),
        answers: count,
        you: count,
        /** Up to three named more than you, most named first. */
        leaders: z.array(opportunityPartySchema).min(1),
    }),
    z.object({
        kind: z.literal("page_slipping"),
        ...keyed,
        url: z.string(),
        title: z.string().nullable(),
        now: count,
        before: count,
    }),
    z.object({
        kind: z.literal("site_to_get_onto"),
        ...keyed,
        domain: z.string(),
        answers: count,
        questions: count,
        leader: opportunityPartySchema,
    }),
    z.object({
        kind: z.literal("question_to_reword"),
        ...keyed,
        promptId: z.string(),
        text: z.string(),
        answers: count,
        you: count,
        /** The names its answers use most; none of them a rival. */
        named: z.array(opportunityPartySchema),
    }),
    z.object({
        kind: z.literal("company_to_track"),
        ...keyed,
        name: z.string(),
        entityKey: z.string(),
        answers: count,
        questions: count,
        /** Other ways the answers write the same company. */
        alsoCalled: z.array(z.string()),
    }),
    z.object({
        kind: z.literal("engine_gap"),
        ...keyed,
        engine: z.string(),
        answers: count,
        you: count,
        leader: opportunityPartySchema,
    }),
]);
export const thinQuestionSchema = z.object({
    promptId: z.string(),
    text: z.string(),
    answers: count,
});
const opportunitiesBase = {
    reportId: z.string(),
    brandId: z.string(),
    brandName: z.string(),
    /** The brand's site, as a registrable domain. */
    domain: z.string(),
    from: isoDate,
    to: isoDate,
    engine: z.string().nullable(),
    /** The days before, of the same length, that pages are compared against. */
    before: z.object({ from: isoDate, to: isoDate }),
    /** The fewest answers in the range for anything here to be worked out. */
    required: count,
    /** The fewest answers a question needs to be compared. */
    perQuestion: count,
};
export const opportunitiesSchema = z.discriminatedUnion("state", [
    z.object({ state: z.literal("insufficient"), ...opportunitiesBase, answers: count }),
    z.object({
        state: z.literal("ready"),
        ...opportunitiesBase,
        answers: count,
        items: z.array(opportunitySchema),
        thin: z.array(thinQuestionSchema),
        /** Whether a model judges the companies the answers name on this server. */
        judged: z.boolean(),
    }),
]);
/** Which answers an opened item is about. */
export const lensBySchema = z.enum(["prompt", "engine", "url", "domain", "entity"]);
export const opportunityLensQuerySchema = reportFilterSchema.extend({
    by: lensBySchema,
    value: z.string().min(1).max(2048),
    otherBrand: z.string().min(1).max(200).optional(),
    otherEntity: z.string().min(1).max(200).optional(),
});
export const opportunityLensSchema = z.object({
    engines: z.array(z.object({ engine: z.string(), answers: count, you: count, other: count })),
    latest: z.array(z.object({
        runId: z.string(),
        promptId: z.string(),
        engine: z.string(),
        day: isoDate,
        you: z.boolean(),
        other: z.boolean(),
    })),
    pages: z.array(z.object({
        url: z.string(),
        domain: z.string().nullable(),
        title: z.string().nullable(),
        citations: count,
        /** Answers citing it that name the other side and not the brand. */
        otherOnly: count,
        own: z.boolean(),
    })),
    questions: z.array(z.object({
        promptId: z.string(),
        text: z.string(),
        answers: count,
        you: count,
        other: count,
    })),
});
/** What blocking a crawler costs: being cited in answers, or only training. */
export const crawlerPurposeSchema = z.enum(["answers", "training"]);
export const crawlerAccessSchema = z.object({
    token: z.string(),
    vendor: z.string(),
    /** The vendor's own site, for its mark. */
    site: z.string(),
    /** The answers it feeds, in the reader's words. */
    feeds: z.string(),
    purpose: crawlerPurposeSchema,
    blocked: z.boolean(),
    /** The robots.txt line that decided it, as written; null when none did. */
    rule: z.string().nullable(),
});
const accessBase = {
    domain: z.string(),
    robotsUrl: z.string(),
    checkedAt: z.string(),
};
export const aiAccessSchema = z.discriminatedUnion("state", [
    z.object({
        state: z.literal("read"),
        ...accessBase,
        crawlers: z.array(crawlerAccessSchema),
    }),
    z.object({
        state: z.literal("no_file"),
        ...accessBase,
        crawlers: z.array(crawlerAccessSchema),
    }),
    z.object({ state: z.literal("unreadable"), ...accessBase, reason: z.string() }),
]);
const windowCountSchema = z.object({
    from: isoDate,
    to: isoDate,
    answers: count,
    you: count,
    other: count,
});
/**
 * One opportunity looked into (DESIGN.md §9, "Opportunities" → The drawer):
 * the same answers as its lens, seen four more ways. Counts and sentences
 * found in the answers only; no model wrote any of it (AGENTS.md §14a).
 */
export const opportunityInsightSchema = z.object({
    /** The gap over the range, and over the same number of days before it. */
    now: windowCountSchema,
    before: windowCountSchema,
    /**
     * Where each side sits in the answers, over only those whose every company
     * was read: a rank among tracked brands alone flatters the brand.
     */
    positions: z.object({
        answers: count,
        youFirst: count,
        otherFirst: count,
        bothNamed: count,
        youAhead: count,
        otherAhead: count,
    }),
    /** The tone of the answers naming the brand; not every answer is scored yet. */
    tone: z.object({
        named: count,
        scored: count,
        positive: count,
        neutral: count,
        negative: count,
        /** The newest sentence of each tone, found in the answer before it was stored. */
        quotes: z.array(z.object({
            runId: z.string(),
            promptId: z.string(),
            engine: z.string(),
            day: isoDate,
            label: z.enum(["positive", "neutral", "negative"]),
            quote: z.string(),
        })),
    }),
    /** What the engines searched before answering, where they say. */
    searches: z.object({
        queries: z.array(z.object({
            query: z.string(),
            answers: count,
            /** Of those, the answers naming the other side and not the brand. */
            otherOnly: count,
            engines: z.array(z.string()),
        })),
        /** Per engine: its answers, and how many said what they searched. */
        engines: z.array(z.object({ engine: z.string(), answers: count, disclosed: count })),
    }),
});
/** The most pages one drawer reads at once: the six it shows. */
export const MAX_READ_PAGES = 6;
export const pageReadingsAskSchema = z.object({
    from: isoDate,
    to: isoDate,
    engine: z.string().optional(),
    urls: z.array(z.url().max(2048)).min(1).max(MAX_READ_PAGES),
});
/** A page's state in its reading: waiting, read, or how it could not be read. */
export const readingStateSchema = z.enum([
    "queued",
    "fetching",
    "read",
    "insufficient_text",
    "refused_by_robots",
    "blocked",
    "not_html",
    "too_large",
    "http_error",
    "unsafe_target",
    "failed",
]);
/**
 * The pages behind an opportunity that its answers cited, each as far as its
 * reading has got. Matched by name, no model: `quote` is the sentence the
 * name was found in, copied from the page.
 */
export const pageReadingsSchema = z.object({
    pages: z.array(z.object({
        url: z.string(),
        state: readingStateSchema,
        failureCode: z.string().nullable(),
        fetchedAt: z.string().nullable(),
        /** When the page reached its state: queued, picked up, or finished. */
        since: z.string(),
        /**
         * Queued or being read for longer than the reader ever takes: nothing
         * is working on it. Decided by the server, so every screen agrees.
         */
        stalled: z.boolean(),
        presence: z.array(z.object({
            brandId: z.string(),
            brandName: z.string(),
            found: z.boolean(),
            listOnly: z.boolean(),
            quote: z.string().nullable(),
        })),
    })),
    /** Pages put on the queue by this ask. */
    queued: count,
});
//# sourceMappingURL=opportunities.js.map